/**
 * filter-options — ordering helpers for toolbar filter dropdowns (Projects, Intakes + Resources).
 *
 * Convention enforced here: the "All …" sentinel is always injected first by the call site, then
 * the remaining values are ordered by these helpers — text values A–Z (case-insensitive), phase
 * values by their numeric suffix, level values by seniority rank. Sorting in code rather than by
 * hand keeps the order stable when new values arrive from the API.
 *
 * NOTE ON PROVENANCE: no SRS FR or architecture doc specifies filter-dropdown ordering (grepped
 * `docs/` — the only "sorted ascending" requirement is the SOW-hours confidence queue in
 * sow-hours-parsing-architecture.md §5.4, which is unrelated). This is a UI consistency
 * improvement, not a customer-confirmed requirement.
 */

/** Sort filter labels A–Z, case- and accent-insensitive. */
export function sortTextOptions(values: readonly string[]): string[] {
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Same ordering as {@link sortTextOptions}, for `{ value, label }` option objects. */
export function sortOptionsByLabel<T extends { label: string }>(options: readonly T[]): T[] {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Leading integer in a label, or +Infinity when there is none (unnumbered labels sort last).
 * Used so "Phase 2" precedes "Phase 10" — a plain A–Z sort would invert them.
 */
export function phaseNumber(label: string): number {
  const match = /\d+/.exec(label);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
}

/** Sort "Phase N" labels by their numeric suffix ascending, falling back to A–Z on a tie. */
export function sortPhaseOptions(values: readonly string[]): string[] {
  return [...values].sort(
    (a, b) =>
      phaseNumber(a) - phaseNumber(b) || a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Seniority progression for the Clockify "Level" custom field. Ranked, NOT alphabetical — same
 * rationale as {@link sortPhaseOptions}: the value implies an order, so it must be ordered by rank.
 *
 * Only the three values Clockify actually emits are ranked (`Junior | Mid | Senior` — confirmed in
 * docs/clockify_users_sample.json and documented on `extractLevel` in
 * packages/resources/services/clockify.service.ts). No other levels are invented here; anything
 * unrecognised sorts last, A–Z.
 */
const LEVEL_RANK: Record<string, number> = {
  junior: 0,
  mid: 1,
  senior: 2,
};

/** Rank of a level label, or +Infinity when unrecognised (unranked levels sort last). */
export function levelRank(label: string): number {
  return LEVEL_RANK[label.trim().toLowerCase()] ?? Number.POSITIVE_INFINITY;
}

/** Sort level labels by seniority ascending (Junior → Senior), falling back to A–Z on a tie. */
export function sortLevelOptions(values: readonly string[]): string[] {
  return [...values].sort(
    (a, b) => levelRank(a) - levelRank(b) || a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}
